import { RichText } from '../../../components/RichText';
import { ATTR_NAMES } from '../../../types/attrNames';
import { CmsProduct } from '../../../types/cmsProduct';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import LightbulbIcon from '../../icons/LightbulbIcon';
import SingSaverMarkIcon from '../../icons/SingsaverMark';
import { ResolvedRow } from '../utils/resolveAttributeRow/resolveAttributeRow';
import { ProductDetailsAttributeListRow } from './ProductDetailsAttributeListRow';
import {
  ProductDetailsRowHeading,
  ProductDetailsRowHeadingLevel,
} from './ProductDetailsRowHeading';
import { ProductDetailsTableKeyValueRow } from './ProductDetailsTableKeyValueRow';

type ProductDetailsAttributeRowProps = {
  row: ResolvedRow;
  isLast: boolean;
  headingLevel: ProductDetailsRowHeadingLevel;
  /** Section title for the row's analytics events (same value the default sections pass). */
  sectionTitle?: string;
  product?: CmsProduct;
  pushDataLayer?: PushDataLayerFn;
};

/**
 * Bullet icons of the Highlights lists, keyed by attribute NAME — the same marks the default
 * Highlights section draws, so the configured STK / BKA sections keep its look.
 */
const LIST_ROW_ICONS: Partial<Record<string, JSX.Element>> = {
  [ATTR_NAMES.QUICK_FACTS]: (
    <SingSaverMarkIcon className="absolute left-0 top-1 size-5 text-fill/secondary" />
  ),
  [ATTR_NAMES.THINGS_TO_CONSIDER]: (
    <LightbulbIcon className="absolute left-0 top-1 size-5 text-campaign/orange-2" />
  ),
};

/** Renders one resolved row by kind — key/value line, titled bullet list, or titled rich-text block. */
export const ProductDetailsAttributeRow: React.FC<
  ProductDetailsAttributeRowProps
> = ({
  row,
  isLast,
  headingLevel,
  sectionTitle,
  product,
  pushDataLayer,
}) => {
  switch (row.kind) {
    case 'value':
      return (
        <ProductDetailsTableKeyValueRow
          label={row.label}
          value={row.value}
          additionalInformation={row.additionalInformation}
          hiddenBorder={isLast}
          sectionTitle={sectionTitle}
          product={product}
          pushDataLayer={pushDataLayer}
        />
      );
    case 'list':
      return (
        <ProductDetailsAttributeListRow
          label={row.label}
          items={row.items}
          headingLevel={headingLevel}
          icon={LIST_ROW_ICONS[row.attributeKey]}
        />
      );
    case 'richText':
      return (
        <div className="flex flex-col gap-spacing/gap/x">
          <ProductDetailsRowHeading level={headingLevel}>
            {row.label}
          </ProductDetailsRowHeading>
          <RichText>{row.html}</RichText>
        </div>
      );
    default:
      return null;
  }
};
